import React from 'react';
import { motion } from 'framer-motion';
import { Star, Quote, BarChart, TrendingUp, DollarSign } from 'lucide-react';

const testimonials = [
  {
    name: 'Sarah Chen',
    role: 'CFO, TechVision Global',
    company: 'TechVision Global',
    image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop&q=80',
    content:
      "After implementing CashFlow AI, we've seen a 40% reduction in processing time and caught $2.3M in potential savings. The AI-driven insights have transformed our financial decision-making process.",
    metrics: {
      savings: '40%',
      efficiency: '2.3M',
      timeframe: '6 months',
    },
    rating: 5,
  },
  {
    name: 'James Anderson',
    role: 'Director of Finance, BuildRight Construction',
    company: 'BuildRight Construction',
    image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&q=80',
    content:
      "The real-time financial insights have been invaluable for our construction projects. We've improved cash flow forecasting accuracy by 85% and reduced invoice processing time by 75%.",
    metrics: {
      accuracy: '85%',
      reduction: '75%',
      timeframe: '3 months',
    },
    rating: 5,
  },
  {
    name: 'Maria Rodriguez',
    role: 'CEO, InnovateMed Solutions',
    company: 'InnovateMed Solutions',
    image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&q=80',
    content:
      "CashFlow AI's predictive analytics helped us optimize our healthcare supply chain, resulting in a 32% cost reduction and 60% faster financial reporting. It's been transformative for our operations.",
    metrics: {
      costs: '32%',
      speed: '60%',
      timeframe: '12 months',
    },
    rating: 5,
  },
];

const caseStudies = [
  {
    company: 'TechVision Global',
    industry: 'Technology',
    challenge: 'Manual financial processes causing delays and missed opportunities',
    solution: 'Implemented AI-driven financial analysis and automation',
    results: [
      '40% reduction in processing time',
      '$2.3M in identified cost savings',
      '85% improvement in forecast accuracy',
    ],
  },
  {
    company: 'BuildRight Construction',
    industry: 'Construction',
    challenge: 'Complex project financials and delayed reporting',
    solution: 'Real-time financial tracking and AI insights',
    results: [
      '75% faster invoice processing',
      '90% reduction in reporting errors',
      '45% improvement in cash flow management',
    ],
  },
];

export function TestimonialSection() {
  return (
    <section className="py-24 bg-dark-bg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl font-bold text-dark-text sm:text-4xl">
            Trusted by Industry Leaders
          </h2>
          <p className="mt-4 text-lg text-dark-muted">
            Join thousands of businesses transforming their financial operations with AI
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={testimonial.name}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-dark-card rounded-lg p-6 shadow-lg border border-dark-border relative"
            >
              <Quote className="absolute top-4 right-4 h-8 w-8 text-primary-400/20" />

              <div className="flex items-center mb-4">
                <img
                  src={testimonial.image}
                  alt={testimonial.name}
                  className="h-12 w-12 rounded-full"
                />
                <div className="ml-4">
                  <h4 className="text-lg font-semibold text-dark-text">{testimonial.name}</h4>
                  <p className="text-sm text-dark-muted">{testimonial.role}</p>
                </div>
              </div>

              <div className="flex mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                ))}
              </div>

              <p className="text-dark-muted mb-4">{testimonial.content}</p>

              <div className="grid grid-cols-3 gap-4 mt-6 pt-4 border-t border-dark-border">
                {Object.entries(testimonial.metrics).map(([key, value], i) => (
                  <div key={key} className="text-center">
                    <p className="text-xl font-bold text-primary-400">{value}</p>
                    <p className="text-sm text-dark-muted capitalize">{key}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="mt-20"
        >
          <h3 className="text-2xl font-bold text-dark-text mb-8 text-center">
            Featured Case Studies
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {caseStudies.map((study, index) => (
              <div
                key={study.company}
                className="bg-dark-card rounded-lg p-6 border border-dark-border"
              >
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-semibold text-dark-text">{study.company}</h4>
                  <span className="px-3 py-1 text-sm bg-primary-400/10 text-primary-400 rounded-full">
                    {study.industry}
                  </span>
                </div>
                <div className="space-y-4">
                  <div>
                    <h5 className="text-sm font-medium text-dark-muted mb-2">Challenge</h5>
                    <p className="text-dark-text">{study.challenge}</p>
                  </div>
                  <div>
                    <h5 className="text-sm font-medium text-dark-muted mb-2">Solution</h5>
                    <p className="text-dark-text">{study.solution}</p>
                  </div>
                  <div>
                    <h5 className="text-sm font-medium text-dark-muted mb-2">Results</h5>
                    <ul className="space-y-2">
                      {study.results.map((result, i) => (
                        <li key={i} className="flex items-center text-dark-text">
                          <TrendingUp className="h-4 w-4 text-primary-400 mr-2" />
                          {result}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="mt-16 text-center"
        >
          <a
            href="#pricing"
            className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-dark-text bg-primary-600 hover:bg-primary-700 transition-colors"
          >
            Start Your Free Trial Today
          </a>
          <p className="mt-4 text-sm text-dark-muted">
            Join thousands of satisfied customers using CashFlow AI
          </p>
        </motion.div>
      </div>
    </section>
  );
}
